import type { FormRAssignmentLine } from '../form-r/form-r-sections.ts';
import {
  formulaChoiceQuestionID,
  numberQuestionID,
  type ProductAnswersByIDAndType,
  productSolidsQuestionID,
  questionnaireFindChemicalSpecificQuestions,
  questionsReplaceCasPlaceholders,
  questionsRequiredFromReleaseProfiles,
} from '../questionnaires.ts';
import { productUsageGalVar, productUsageLbVar, rainfallVar } from '../formulas/formula-utils.ts';
import {
  type EmissionsPeriodDetails,
  type EmissionsPeriodDetailsWithoutFixed,
  periodPotential,
  periodSplitIntoSubPeriods,
  periodsProduce,
} from './emissions-dates.ts';
import type {
  ChemicalBrief,
  QuestionBriefNumber,
  QuestionBriefProductSolids,
  QuestionBriefProductUsage,
  QuestionBriefReleaseProfileFormulaChoice,
  ReleaseProfileProcessChemical,
  ReleaseProfileAnswerValues,
  Warning,
} from '../../types/tri.ts';
import type {
  FormulaOutputType,
  ReleaseProfileFormulaChoiceID,
  ReleaseProfileQuestion,
  ReleaseProfilesAndVariables,
} from '../../types/release-profiles.ts';
import type { EmissionUnitTypeWithConfig, EmissionUnitWithConfig } from '../../types/emissions.ts';
import { releaseProfileAnnotatableVariableNames, releaseProfilesIterateChemicals } from '../release-profiles.ts';
import type {
  CasOrCategory,
  EmissionsAnswer,
  EmissionsPeriod,
  EmissionsTrackingMode,
  EmissionsUnit,
  EmissionsUnitID,
  EmissionsUnitTypeID,
  FacilityID,
  ProductID,
  ReleaseProfileID,
  ReleaseProfileQuestionID,
  ReleaseProfileVariableID,
  UsageTypeID,
  UsageUnits,
} from '../../types/db.ts';
import type { ISO8601DateOnly } from '../../../cross-app-shared/types/common.ts';
import type { QuestionnaireFormulaChoicesContainer } from '../../types/questionnaires.ts';
import { type CalculationLine, chemicalCalculationsExecute } from '../releases-calculate.ts';
import { jsonStableStringify } from '../json-stable-stringify.ts';
import type {
  EmissionsQuestionsAndCalculationResultsForPeriod,
  EmissionsInputTableQuestion,
  EmissionsUnitWithQuestionAndAnswerData,
  EmissionsFixedAnswer,
} from '../reports/emissions-to-table.ts';
import { usageAmountToPounds } from '../formulas/units.ts';

export type EmissionsQuestionBrief =
  | QuestionBriefNumber
  | QuestionBriefProductSolids
  | QuestionBriefProductUsage
  | QuestionBriefReleaseProfileFormulaChoice;

// ReleaseProfileProcessWithChildren minimized for just what the emissions tool needs:
export interface EmissionsProcessAndProfiles {
  formRLine: FormRAssignmentLine | null;
  processName: string;
  facilityID: FacilityID;
  profiles: {
    releaseProfileID: ReleaseProfileID;
    releaseProfileName: string;
    thresholdOrRelease: 'release' | 'threshold' | 'both';
    thresholdUsageTypeID: UsageTypeID | null;
    products: {
      productID: ProductID;
      productName: string;
      specificGravity: number | null;
      usageUnits: UsageUnits;
      solidsPercentageByWeight: number | null;
      solidsPercentageByVolume: number | null;
      archivedAt: ISO8601DateOnly | null;
      chemicalsExcluded: CasOrCategory[];
      chemicals: ReleaseProfileProcessChemical[];
    }[];
  }[];
}

export function emissionsUnitRelevantProfilesAndChemicals(
  unitType: EmissionUnitTypeWithConfig,
  unit: EmissionUnitWithConfig,
  releaseProfilesAndVariables: ReleaseProfilesAndVariables,
  chemicalsByCasOrCategory: Partial<Record<CasOrCategory, Pick<ChemicalBrief, 'casOrCategory' | 'name'>>>,
): { chemicals: Map<CasOrCategory, string>; process: EmissionsProcessAndProfiles } {
  const chemicals = new Map<CasOrCategory, string>([
    ['PM', 'PM'],
    ['PM10', 'PM10'],
    ['PM2.5', 'PM2.5'],
    ['PM Filterable', 'PM Filterable'],
    ['PM10 Filterable', 'PM10 Filterable'],
    ['PM2.5 Filterable', 'PM2.5 Filterable'],
    ['PM Condensable', 'PM Condensable'],
    ['VOC', 'VOC'],
    ['CO2e', 'CO2e'],
    ['F', 'Fluorides'],
    // These categories will not get mapped automatically, so anything we directly output will have been provided
    // by the user explicitly on a chemical profile or product:
    ['NOx', 'NOx'],
    ['SOx', 'SOx'],
  ] as [CasOrCategory, string][]);

  for (const product of unit.products) {
    for (const { casOrCategoryOriginal, chemicalName } of product.chemicals) {
      chemicals.set(casOrCategoryOriginal, chemicalName);
    }
  }

  const uniqueReleaseProfiles = new Map<ReleaseProfileID, string>();
  for (const { releaseProfileID, name: releaseProfileName } of unitType.releaseProfiles) {
    uniqueReleaseProfiles.set(releaseProfileID, releaseProfileName);
  }
  for (const { releaseProfileID, name: releaseProfileName } of unit.limitsWithReleaseProfiles) {
    uniqueReleaseProfiles.set(releaseProfileID, releaseProfileName);
  }

  const profiles: EmissionsProcessAndProfiles['profiles'] = [];
  for (const [releaseProfileID, releaseProfileName] of uniqueReleaseProfiles.entries()) {
    profiles.push({
      releaseProfileID,
      releaseProfileName,
      thresholdOrRelease: 'both',
      thresholdUsageTypeID: null,
      products: unit.products.map((product) => {
        return {
          productID: product.productID,
          productName: product.name,
          specificGravity: product.specificGravity,
          usageUnits: product.usageUnits,
          solidsPercentageByWeight: product.solidsPercentageByWeight,
          solidsPercentageByVolume: product.solidsPercentageByVolume,
          archivedAt: product.archivedAt,
          chemicalsExcluded: [],
          chemicals: product.chemicals,
        };
      }),
    });

    const profile = releaseProfilesAndVariables.releaseProfiles.find((p) => p.releaseProfileID === releaseProfileID);
    if (profile) {
      const profileChemicals = releaseProfilesIterateChemicals(
        releaseProfilesAndVariables,
        // Usage mixes, if used, will need to be based on the products and usage assigned to the unit. Therefore, we
        // will have already captured the relevant product chemicals in emissionsUnitToQuestionsAnnotated, and we don't need
        // the usage mix results here:
        {},
        profile.releaseProfileID,
      );

      for (const casOrCategory of profileChemicals) {
        if (!chemicals.has(casOrCategory)) {
          chemicals.set(casOrCategory, chemicalsByCasOrCategory[casOrCategory]?.name ?? casOrCategory);
        }
      }
    }
  }

  const process: EmissionsProcessAndProfiles = {
    formRLine: null,
    processName: `${unit.name} - ${unitType.name}`,
    facilityID: unit.facilityID,
    profiles,
  };

  return { chemicals, process };
}

export type EmissionsQuestionSource =
  | 'unitType'
  | 'unit'
  | 'product'
  | 'facilityIntervalAnswers'
  | 'unitTypeIntervalAnswers'
  | 'unitIntervalAnswers';

export interface EmissionsQuestionAnnotated<
  Question extends EmissionsQuestionBrief = EmissionsQuestionBrief,
  Source = EmissionsQuestionSource,
> {
  question: Question;
  unitTypeConfig:
    | EmissionUnitTypeWithConfig['variableConfigs'][number]
    | EmissionUnitTypeWithConfig['predefinedVariableConfigs'][number]
    | EmissionUnitTypeWithConfig['formulaChoiceConfigs'][number]
    | null;
  source: Source;
}

function unitToAcceptableOutputTypes(
  unit: Pick<EmissionUnitWithConfig, 'trackingMode' | 'limitsWithReleaseProfiles'>,
): Set<FormulaOutputType> {
  const acceptableOutputType = new Set<FormulaOutputType>();
  if (unit.trackingMode === 'trackingActuals') {
    acceptableOutputType.add('actual');
    acceptableOutputType.add('potential');
  } else {
    acceptableOutputType.add('potential');
  }

  if (unit.limitsWithReleaseProfiles.length > 0) {
    acceptableOutputType.add('complianceNumerator');
    acceptableOutputType.add('complianceDenominator');
  }

  return acceptableOutputType;
}

function unitPeriodToAcceptableOutputTypes(
  unit: Pick<EmissionUnitWithConfig, 'trackingMode' | 'limitsWithReleaseProfiles'>,
  period: EmissionsPeriod | 'potential' | 'fixed@all',
): Set<FormulaOutputType> {
  if (period === 'potential') {
    return new Set(['potential']);
  }

  const acceptableOutputTypes = new Set<FormulaOutputType>();
  if (unit.trackingMode === 'trackingActuals') {
    acceptableOutputTypes.add('actual');
  } else if (unit.trackingMode === 'rollPteIntoActuals') {
    acceptableOutputTypes.add('potential');
  }

  if (unit.limitsWithReleaseProfiles.length > 0) {
    acceptableOutputTypes.add('complianceNumerator');
    acceptableOutputTypes.add('complianceDenominator');
  }

  return acceptableOutputTypes;
}

export interface EmissionsUnitQuestionnaireSpec {
  unitType: EmissionUnitTypeWithConfig;
  unit: EmissionUnitWithConfig;
  chemicalsRelevant: CasOrCategory[];
  process: EmissionsProcessAndProfiles;
  releaseProfilesAndVariables: ReleaseProfilesAndVariables;
  chemicalNames: Partial<Record<CasOrCategory, Pick<ChemicalBrief, 'casOrCategory' | 'name'>>>;
  questionListCache: Map<string, EmissionsQuestionBrief[]>;
  questionVisibilityCache: Map<`${string}-${ReleaseProfileQuestionID}`, boolean>;
}

function emissionsUnitToQuestionBriefs(
  {
    unit,
    chemicalsRelevant,
    process,
    releaseProfilesAndVariables,
    chemicalNames,
    questionListCache,
  }: EmissionsUnitQuestionnaireSpec,
  periodSpecificFormulaChoices: QuestionnaireFormulaChoicesContainer,
): EmissionsQuestionBrief[] {
  const key = jsonStableStringify(periodSpecificFormulaChoices);
  if (questionListCache.has(key)) {
    return questionListCache.get(key)!;
  }

  const acceptableOutputType = unitToAcceptableOutputTypes(unit);
  const questions = questionsRequiredFromReleaseProfiles(
    { type: 'emissions', acceptableOutputType },
    chemicalsRelevant,
    periodSpecificFormulaChoices,
    [process],
    {
      releaseProfiles: releaseProfilesAndVariables.releaseProfiles,
      questions: releaseProfilesAndVariables.questions,
      mixCalculations: [], // Mix calculations are unused and likely to be removed, so we can skip those.
      wasteStreams: [], // Waste streams are too wrapped up in TRI to be sensible in emissions units.
    },
    releaseProfileAnnotatableVariableNames(releaseProfilesAndVariables),
  ).filter(
    (q) =>
      q.type !== 'wasteStreamAttestedZero' && q.type !== 'wasteStreamAllocation' && q.type !== 'wasteStreamDirectInput',
  );
  questionListCache.set(key, questions);
  questionsReplaceCasPlaceholders(questions, (casOrCategory) => chemicalNames[casOrCategory]?.name ?? null);
  return questions;
}

function emissionsQuestionIsVisible(
  spec: EmissionsUnitQuestionnaireSpec,
  question: EmissionsQuestionBrief,
  period: EmissionsPeriod | 'potential' | 'fixed@all',
  periodStart: ISO8601DateOnly,
  periodSpecificFormulaChoices: QuestionnaireFormulaChoicesContainer,
): boolean {
  const appliesToPeriod = emissionsQuestionAppliesToPeriod(period, question);
  if (!appliesToPeriod) {
    return false;
  }

  if (
    question.type === 'productSolids' ||
    question.type === 'productUsage' ||
    (question.type === 'number' && question.isProductSpecific)
  ) {
    if (
      spec.unit.products.some((p) => p.productID === question.productID && p.archivedAt && p.archivedAt <= periodStart)
    ) {
      return false;
    }
  }

  const key = `${jsonStableStringify(periodSpecificFormulaChoices)}-${question.questionID}` as const;
  if (spec.questionVisibilityCache.has(key)) {
    return spec.questionVisibilityCache.get(key)!;
  }

  const isVisible = !!emissionsUnitToQuestionBriefs(spec, periodSpecificFormulaChoices).find(
    (q) => q.questionID === question.questionID,
  );
  spec.questionVisibilityCache.set(key, isVisible);
  return isVisible;
}

export function emissionsUnitToQuestionsAnnotated(spec: EmissionsUnitQuestionnaireSpec): EmissionsQuestionAnnotated[] {
  const { unitType, unit, chemicalsRelevant, releaseProfilesAndVariables } = spec;

  const releaseProfileQuestionByVariableID = new Map<ReleaseProfileVariableID, ReleaseProfileQuestion>(
    releaseProfilesAndVariables.questions.map((q) => [q.releaseProfileVariableID, q]),
  );

  const unitTypeQuestionConfigsByQuestionID = new Map<
    ReleaseProfileQuestionID | string,
    | EmissionUnitTypeWithConfig['variableConfigs'][number]
    | EmissionUnitTypeWithConfig['predefinedVariableConfigs'][number]
    | EmissionUnitTypeWithConfig['formulaChoiceConfigs'][number]
  >();

  for (const unitTypeVariableConfig of unitType.variableConfigs) {
    const question = releaseProfileQuestionByVariableID.get(unitTypeVariableConfig.releaseProfileVariableID);
    if (!question) {
      continue;
    }

    const baseQuestionID = question.definition.questionID;
    const chemicals = question.definition.isChemicalSpecific ? chemicalsRelevant : [null];
    const productIds = question.definition.isProductSpecific ? unit.products.map((p) => p.productID) : [null];

    for (const casOrCategory of chemicals) {
      for (const productID of productIds) {
        const questionID = numberQuestionID(baseQuestionID, casOrCategory, productID);
        unitTypeQuestionConfigsByQuestionID.set(questionID, unitTypeVariableConfig);
      }
    }
  }

  for (const unitTypePredefinedVariableConfig of unitType.predefinedVariableConfigs) {
    unitTypeQuestionConfigsByQuestionID.set(
      unitTypePredefinedVariableConfig.predefinedVariableName,
      unitTypePredefinedVariableConfig,
    );
  }

  for (const unitTypeFormulaChoiceConfig of unitType.formulaChoiceConfigs) {
    const questionID = formulaChoiceQuestionID(unitTypeFormulaChoiceConfig.choiceListID);
    unitTypeQuestionConfigsByQuestionID.set(questionID, unitTypeFormulaChoiceConfig);
  }

  return emissionsUnitToQuestionBriefs(
    spec,
    // Passing an empty 'periodSpecificFormulaChoices' will cause us to generate all possible questions:
    {},
  ).map((question): EmissionsQuestionAnnotated => {
    let unitTypeConfig;
    if (question.type === 'productUsage') {
      if (question.units === 'pounds') {
        unitTypeConfig =
          unitTypeQuestionConfigsByQuestionID.get(productUsageLbVar) ??
          unitTypeQuestionConfigsByQuestionID.get(productUsageGalVar) ??
          null;
      } else {
        unitTypeConfig =
          unitTypeQuestionConfigsByQuestionID.get(productUsageGalVar) ??
          unitTypeQuestionConfigsByQuestionID.get(productUsageLbVar) ??
          null;
      }
    } else if (question.questionID === 'question_rainfall') {
      unitTypeConfig = unitTypeQuestionConfigsByQuestionID.get(rainfallVar) ?? null;
    } else {
      unitTypeConfig = unitTypeQuestionConfigsByQuestionID.get(question.questionID) ?? null;
    }

    let source: EmissionsQuestionAnnotated['source'];
    if (question.type === 'productSolids') {
      source = 'product';
    } else if (unitTypeConfig?.frequency === 'once') {
      source = 'unitType';
    } else if (unitTypeConfig?.frequency === 'perUnit') {
      source = 'unit';
    } else if (unitTypeConfig?.frequency === 'perFacilityPerInterval') {
      source = 'facilityIntervalAnswers';
    } else if (unitTypeConfig?.frequency === 'perUnitTypePerInterval') {
      source = 'unitTypeIntervalAnswers';
    } else if (unitTypeConfig?.frequency === 'perInterval' || !unitTypeConfig?.frequency) {
      source = 'unitIntervalAnswers';
    } else {
      const unexpected: never = unitTypeConfig?.frequency;
      console.error('emissionsUnitToQuestionsAnnotated: Unexpected config frequency:', unexpected);
      source = 'unitIntervalAnswers';
    }

    return {
      question,
      unitTypeConfig,
      source,
    };
  });
}

export function emissionsFixedAnswersExtract(
  questions: EmissionsQuestionAnnotated[],
  answersFromUnit: EmissionsAnswerByPeriodAndQuestion,
  answersFromUnitType: EmissionsAnswerByPeriodAndQuestion,
) {
  const splitDatesFromFixedAnswersSet = new Set<ISO8601DateOnly>();
  const unitQuestionIDs = new Set<ReleaseProfileQuestionID>();
  const unitTypeQuestionIDs = new Set<ReleaseProfileQuestionID>();
  for (const question of questions) {
    if (question.source === 'unit') {
      unitQuestionIDs.add(question.question.questionID);
    } else if (question.source === 'unitType') {
      unitTypeQuestionIDs.add(question.question.questionID);
    }
  }

  function fixedAnswersFrom(
    answers: EmissionsAnswerByPeriodAndQuestion,
    questionIDs: Set<ReleaseProfileQuestionID>,
  ): EmissionsFixedAnswer[] {
    const fixedAnswers: EmissionsFixedAnswer[] = [];
    if (questionIDs.size === 0) {
      return fixedAnswers;
    }
    for (const [key, answer] of Object.entries(answers)) {
      if (answer === undefined || answer === '') {
        continue;
      }
      // See periodFixedKey:
      const match = /^fixed@(\d{4}-\d{2}-\d{2})-(question_.+)$/.exec(key);
      if (!match) {
        continue;
      }
      const date = match[1] as ISO8601DateOnly;
      const questionID = match[2] as ReleaseProfileQuestionID;
      if (!questionIDs.has(questionID)) {
        continue;
      }
      splitDatesFromFixedAnswersSet.add(date);
      fixedAnswers.push({ questionID, date, answer });
    }
    return fixedAnswers;
  }

  const unitFixedAnswers = fixedAnswersFrom(answersFromUnit, unitQuestionIDs);
  const unitTypeFixedAnswers = fixedAnswersFrom(answersFromUnitType, unitTypeQuestionIDs);

  return { splitDatesFromFixedAnswersSet, unitFixedAnswers, unitTypeFixedAnswers };
}

/** True if a fixed answer effective from the start of `date` must split `period` so no period straddles two answers */
export function emissionsFixedAnswerDateSplitsPeriod(
  period: { start: ISO8601DateOnly; end: ISO8601DateOnly },
  date: ISO8601DateOnly,
): boolean {
  return (
    // We don't need to split the period if the date lands on the start of the period (i.e. assume dates in
    // are effective from the start of that day):
    period.start < date &&
    // But if it lands on the end date, we'll need a 1 day period to handle it:
    period.end >= date
  );
}

export function emissionsUnitAnnotateWithQuestionAndAnswerData({
  unitType,
  unit,
  unitSplits,
  answersFromUnit,
  answersFromUnitType,
  answersFromFacility,
  releaseProfilesAndVariables,
  chemicalNames,
  today,
}: {
  unitType: EmissionUnitTypeWithConfig;
  unit: EmissionUnitWithConfig;
  unitSplits: Partial<Record<EmissionsPeriod, ISO8601DateOnly[]>>;
  answersFromUnitType: EmissionsAnswerByPeriodAndQuestion;
  answersFromFacility: EmissionsAnswerByPeriodAndQuestion;
  answersFromUnit: EmissionsAnswerByPeriodAndQuestion;
  releaseProfilesAndVariables: ReleaseProfilesAndVariables;
  chemicalNames: Partial<Record<CasOrCategory, Pick<ChemicalBrief, 'casOrCategory' | 'name'>>>;
  today: ISO8601DateOnly;
}): EmissionsUnitWithQuestionAndAnswerData {
  const { chemicals, process } = emissionsUnitRelevantProfilesAndChemicals(
    unitType,
    unit,
    releaseProfilesAndVariables,
    chemicalNames,
  );
  const questions = emissionsUnitToQuestionsAnnotated({
    unitType,
    unit,
    chemicalsRelevant: Array.from(chemicals.keys()),
    process,
    releaseProfilesAndVariables,
    chemicalNames,
    questionListCache: new Map(),
    questionVisibilityCache: new Map(),
  });

  const { splitDatesFromFixedAnswersSet, unitFixedAnswers, unitTypeFixedAnswers } = emissionsFixedAnswersExtract(
    questions,
    answersFromUnit,
    answersFromUnitType,
  );
  // Reverse chronological sort is needed for correctness of emissionsGatherAnswersForPeriod:
  unitFixedAnswers.sort((a, b) => b.date.localeCompare(a.date));
  unitTypeFixedAnswers.sort((a, b) => b.date.localeCompare(a.date));

  const periodsRaw = Array.from(periodsProduce(unit.trackingFrequency, unit.entriesStartAt, today));
  const periods = periodsRaw
    .flatMap((period): EmissionsPeriodDetails[] => {
      const splitDates = (unitSplits[period.period] ?? []).slice();

      // We expect splitDatesFromFixedAnswersSet to be very small (i.e. 0 for most units, growing at most 1 element
      // per year for units that use this), so the linear scan here should be fine:
      for (const date of splitDatesFromFixedAnswersSet) {
        if (emissionsFixedAnswerDateSplitsPeriod(period, date) && !splitDates.includes(date)) {
          splitDates.push(date);
        }
      }

      if (splitDates.length > 0) {
        splitDates.sort();
        return periodSplitIntoSubPeriods(period, splitDates);
      }
      return [period];
    })
    .reverse();
  periods.unshift(periodPotential(today));

  return {
    emissionsUnitID: unit.emissionsUnitID,
    unit,
    unitType,
    periods,
    process,
    chemicals: Array.from(chemicals.keys()),
    answersFromUnit,
    answersFromFacility: answersFromFacility,
    answersFromUnitType: answersFromUnitType,
    questions,
    unitFixedAnswers,
    unitTypeFixedAnswers,
  };
}

/** One cell of the questionnaire: a question asked in one of the unit's columns. */
export type EmissionsAnswerKey = `${EmissionsPeriod | 'potential' | 'fixed@all'}-${ReleaseProfileQuestionID}`;

export type EmissionsAnswerByPeriodAndQuestion = Partial<Record<EmissionsAnswerKey, EmissionsAnswer['answer'] | ''>>;

/**
 * Whether one questionnaire cell has been answered, and which column it was asked in — which is not always the period
 * that read it, as a unit rolling its PTE into its actuals computes every period out of the PTE column.
 */
export interface EmissionsCellState {
  column: EmissionsPeriod | 'potential' | 'fixed@all';
  source: EmissionsQuestionSource;
  isAnswered: boolean;
}

/**
 * Which column of the questionnaire holds the answers one output type is computed from.
 *
 * Potential emissions describe the unit as it is configured rather than what a calendar period recorded, so their
 * inputs are answered once, in the PTE column. Everything else is answered against the period it measures — including
 * the compliance metrics of a unit that rolls its PTE into its actuals, whose emissions come from the PTE column but
 * whose metrics still measure that period's own throughput.
 */
export function emissionsAnswersPeriodForOutput(
  period: EmissionsPeriod | 'potential' | 'fixed@all',
  outputType: FormulaOutputType,
): EmissionsPeriod | 'potential' | 'fixed@all' {
  return outputType === 'potential' ? 'potential' : period;
}

/**
 * Whether a period asks a question at all — i.e. whether the questionnaire has a cell for it.
 *
 * This is the mirror of `emissionsAnswersPeriodForOutput`: a question is asked in the column the outputs that want it
 * are computed from. A question wanted only by a potential output is therefore asked once, in the PTE column, and one
 * wanted by an actual or compliance output is asked in each period. A question wanted by both is asked in both, and
 * the two answers are independent — that is the point, since they describe different things.
 *
 * The 'fixed@all' column holds the fixed unit and unit type answers, which feed every column, so it asks every question.
 *
 * Questions no output of this unit wants never reach here: `emissionsUnitToQuestionsAnnotated` has already dropped them.
 */
function emissionsQuestionAppliesToPeriod(
  period: EmissionsPeriod | 'potential' | 'fixed@all',
  question: Pick<EmissionsQuestionBrief, 'formulaOutputTypes'>,
): boolean {
  if (period === 'fixed@all') {
    return true;
  }

  if (period === 'potential') {
    return question.formulaOutputTypes.includes('potential');
  }

  return question.formulaOutputTypes.some((outputType) => outputType !== 'potential');
}

export interface EmissionsUnitCompletionStats {
  totalCells: number;
  answeredCells: number;
  totalDuePeriods: number;
  completePeriods: number;
  duePeriodsWithAnyAnswer: number;
}

export interface EmissionsUnitCompletionStatFromList extends EmissionsUnitCompletionStats {
  emissionsUnitID: EmissionsUnitID;
  unitName: string;
  emissionsUnitTypeID: EmissionsUnitTypeID;
  trackingFrequency: EmissionsUnit['trackingFrequency'];
}

/**
 * How much of a unit's questionnaire is filled in, rolled up across the periods it is asked about.
 *
 * Each period is counted on the cells it asks in its own column, which is the grid the user types into. A period can
 * *read* another column — a unit that rolls its PTE into its actuals computes every one of its periods out of the PTE
 * column — and counting those would charge the unit for that one column once per period reading it. The PTE column
 * is itself one of the periods, so nothing it asks for goes uncounted.
 */
export function emissionsUnitCompletionStats(
  results: Iterable<Pick<EmissionsPeriodQuestionnaireState, 'period' | 'questionsChecked'>>,
  questionVenue: 'cellQuestionsOnly' | 'allQuestions',
  // The date the periods are judged against; a period still running has not fallen behind, and is left out of every
  // count below rather than counted as unanswered. Pass null to count them all.
  today: ISO8601DateOnly | null,
): EmissionsUnitCompletionStats {
  let totalCells = 0;
  let answeredCells = 0;
  let completePeriods = 0;
  let duePeriodsWithAnyAnswer = 0;
  let totalDuePeriods = 0;

  for (const { period, questionsChecked } of results) {
    // A period that has not closed yet is not part of these counts, PTE is always included.
    if (period.type !== 'potential' && today !== null && period.end >= today) {
      continue;
    }

    let periodTotal = 0;
    let periodAnswered = 0;
    for (const cell of questionsChecked.values()) {
      if (
        questionVenue === 'cellQuestionsOnly' &&
        (cell.source === 'unitType' || cell.source === 'unit' || cell.source === 'product')
      ) {
        continue;
      }

      if (cell.column !== period.period) {
        continue;
      }

      periodTotal++;
      if (cell.isAnswered) {
        periodAnswered++;
      }
    }

    totalCells += periodTotal;
    answeredCells += periodAnswered;

    // A period a unit asks nothing of can neither be completed nor be late — a unit rolling its PTE into its actuals
    // asks nothing of most of its periods, computing them from the PTE column instead.
    if (periodTotal === 0) {
      continue;
    }

    totalDuePeriods++;
    if (periodAnswered === periodTotal) {
      completePeriods++;
    }
    if (periodAnswered > 0) {
      duePeriodsWithAnyAnswer++;
    }
  }

  return {
    totalCells,
    answeredCells,
    totalDuePeriods,
    completePeriods,
    duePeriodsWithAnyAnswer,
  };
}

export function calculateEmissions(
  { emissionsUnitID, trackingMode }: { emissionsUnitID: EmissionsUnitID; trackingMode: EmissionsTrackingMode },
  profilesAndVariables: ReleaseProfilesAndVariables,
  chemicals: Set<CasOrCategory>,
  chemicalSpecificQuestions: Map<ReleaseProfileQuestionID, Set<CasOrCategory>>,
  { period, start: periodStart, end: periodEnd }: EmissionsPeriodDetailsWithoutFixed,
  inputsAndOutputTypes: {
    outputType: FormulaOutputType;
    releaseProfileAnswers: ReleaseProfileAnswerValues;
    releaseProfileChemicalNaAnswers: Partial<Record<`question_${string}`, boolean>>;
    releaseProfileFormulaChoices: QuestionnaireFormulaChoicesContainer;
    productUsage: Map<ProductID, { totalPoundsUsed: number; totalGallonsUsed: number | null }>;
    process: EmissionsProcessAndProfiles;
  }[],
): {
  calculationLines: CalculationLine[];
  warnings: Warning[];
} {
  const periodHours =
    (new Date(`${periodEnd}T00:00:00Z`).getTime() - new Date(`${periodStart}T00:00:00Z`).getTime()) / 3_600_000 + 24;

  const warnings: Warning[] = [];
  const calculationLines: CalculationLine[] = [];

  for (const inputs of inputsAndOutputTypes) {
    const pteScaling =
      trackingMode === 'rollPteIntoActuals' && inputs.outputType === 'potential' && period !== 'potential'
        ? { periodHours }
        : null;
    for (let item of chemicalCalculationsExecute(
      {
        type: 'emissions',
        acceptableOutputType: inputs.outputType,
        emissionsUnit: { emissionsUnitID, period },
        pteScaling,
      },
      profilesAndVariables,
      inputs.productUsage,
      chemicals,
      new Map(), // TRI category mappings are only relevant to TRI.
      [inputs.process],
      {
        releaseProfileAnswers: inputs.releaseProfileAnswers,
        releaseProfileChemicalNaAnswers: inputs.releaseProfileChemicalNaAnswers,
        releaseProfileFormulaChoices: inputs.releaseProfileFormulaChoices,
        wasteTransfers: {}, // Waste transfers are only relevant to TRI, there is no way to represent them in the emissions tool.
      },
      {}, // Usage mixes are not currently supported, as right now we can only calculate them by using TRI data.
      {}, // Waste streams are only relevant to TRI, there is no way to represent them in the emissions tool.
      chemicalSpecificQuestions,
    )) {
      if ('warningID' in item) {
        warnings.push(item);
      } else if ('type' in item && (item.type === 'productSeen' || item.type === 'notApplicable')) {
        // Nothing to do.
      } else {
        if (pteScaling) {
          item = {
            ...item,
            formulaOutputType: 'actual',
          };
        }

        calculationLines.push(item);

        // NOx and SOx are the only real emissions categories we have. Any other category mappings will need to be defined
        // by the user in our report builder:
        if (item.casOrCategoryOriginal === '10102-43-9' || item.casOrCategoryOriginal === '10102-44-0') {
          calculationLines.push({
            ...item,
            lineID: item.lineID + 0.1,
            casOrCategoryMapped: 'NOx' as CasOrCategory,
          });
        } else if (item.casOrCategoryOriginal === '7446-09-5' || item.casOrCategoryOriginal === '7446-11-9') {
          calculationLines.push({
            ...item,
            lineID: item.lineID + 0.2,
            casOrCategoryMapped: 'SOx' as CasOrCategory,
          });
        }
      }
    }
  }

  return {
    warnings,
    calculationLines,
  };
}

function emissionsGatherAnswersForPeriod(
  unit: EmissionsUnitWithQuestionAndAnswerData,
  period: EmissionsPeriodDetails,
  productAnswersMap: ProductAnswersByIDAndType,
): {
  answersMapWithAnswersFromConfig: EmissionsAnswerByPeriodAndQuestion;
  formulaChoicesByPeriod: Map<EmissionsPeriod | 'potential' | 'fixed@all', QuestionnaireFormulaChoicesContainer>;
} {
  const answersMapWithAnswersFromConfig: EmissionsAnswerByPeriodAndQuestion = {};
  const formulaChoicesByPeriod = new Map<
    EmissionsPeriod | 'potential' | 'fixed@all',
    QuestionnaireFormulaChoicesContainer
  >();
  // The answer column and the effective configuration date are independent. Rolled actuals read
  // interval inputs from PTE, but fixed inputs must describe this historical period, not today's PTE.
  // The undated fixed row deliberately reads only the undated defaults.
  const configurationDate = period.type === 'fixed' ? period.date : period.start;
  const columns: {
    column: EmissionsPeriod | 'potential' | 'fixed@all';
    start: ISO8601DateOnly | null;
  }[] = [
    { column: 'potential', start: configurationDate },
    ...(period.period === 'potential' ? [] : [{ column: period.period, start: configurationDate }]),
  ];
  // Gather all possible answers. Note that this may populate answers we never read. calculateEmissions is ultimately
  // responsible for picking out the relevant answers:
  for (const { column, start } of columns) {
    const periodSpecificFormulaChoices: QuestionnaireFormulaChoicesContainer = {};

    for (const question of unit.questions) {
      const key = `${column}-${question.question.questionID}` as const;
      let value: EmissionsAnswer['answer'] | '' | undefined;

      if (question.source === 'product') {
        if (question.question.type === 'productSolids') {
          value = productAnswersMap[`${question.question.productID}-${question.question.subType}`];
        } else {
          console.error('Unexpected question type for product source: ', question.question.type);
        }
      } else if (question.source === 'unitType') {
        value =
          // Sorted in emissionsUnitAnnotateWithQuestionAndAnswerData to ensure we find the newest applicable fixed-date answer first:
          unit.unitTypeFixedAnswers.find(
            (a) => start && start >= a.date && a.questionID === question.question.questionID,
          )?.answer ?? unit.answersFromUnitType[`fixed@all-${question.question.questionID}`];
      } else if (question.source === 'unit') {
        value =
          // Sorted in emissionsUnitAnnotateWithQuestionAndAnswerData to ensure we find the newest applicable fixed-date answer first:
          unit.unitFixedAnswers.find((a) => start && start >= a.date && a.questionID === question.question.questionID)
            ?.answer ?? unit.answersFromUnit[`fixed@all-${question.question.questionID}`];
      } else if (question.source === 'unitIntervalAnswers') {
        value = unit.answersFromUnit[key];
      } else if (question.source === 'facilityIntervalAnswers') {
        value = unit.answersFromFacility[key];
      } else if (question.source === 'unitTypeIntervalAnswers') {
        value = unit.answersFromUnitType[key];
      } else {
        const unexpected: never = question.source;
        console.error(`Unexpected question source: ${unexpected}`);
        value = undefined;
      }

      answersMapWithAnswersFromConfig[key] = value;
      if (
        question.question.type === 'releaseProfileFormulaChoice' &&
        typeof value === 'string' &&
        value.startsWith('formula_choice_')
      ) {
        periodSpecificFormulaChoices[question.question.choiceListID] = value as ReleaseProfileFormulaChoiceID;
      }
    }

    formulaChoicesByPeriod.set(column, periodSpecificFormulaChoices);
  }

  return { answersMapWithAnswersFromConfig, formulaChoicesByPeriod };
}

function emissionsAnswersPrepForCalculations(
  periodStart: ISO8601DateOnly,
  questions: EmissionsQuestionAnnotated[],
  process: EmissionsProcessAndProfiles,
  answersMap: EmissionsAnswerByPeriodAndQuestion,
  formulaChoicesByPeriod: Map<EmissionsPeriod | 'potential' | 'fixed@all', QuestionnaireFormulaChoicesContainer>,
  spec: EmissionsUnitQuestionnaireSpec,
  column: EmissionsPeriod | 'potential' | 'fixed@all',
  questionsChecked: Map<EmissionsAnswerKey, EmissionsCellState>,
): {
  releaseProfileAnswers: ReleaseProfileAnswerValues;
  releaseProfileChemicalNaAnswers: Partial<Record<`question_${string}`, boolean>>;
  releaseProfileFormulaChoices: QuestionnaireFormulaChoicesContainer;
  productUsage: Map<ProductID, { totalPoundsUsed: number; totalGallonsUsed: number | null }>;
  process: EmissionsProcessAndProfiles;
} {
  // Build the set of product IDs archived before this period so we can skip their usage and exclude
  // them from the process structure fed into calculation. Historic periods before the archival date
  // will still include the product's contributions.
  const archivedProductIds = new Set<ProductID>();
  for (const profile of process.profiles) {
    for (const product of profile.products) {
      if (product.archivedAt && product.archivedAt <= periodStart) {
        archivedProductIds.add(product.productID);
      }
    }
  }

  const releaseProfileAnswers: ReleaseProfileAnswerValues = {};
  const releaseProfileChemicalNaAnswers: Partial<Record<ReleaseProfileQuestionID, boolean>> = {};
  const releaseProfileFormulaChoices: QuestionnaireFormulaChoicesContainer = {};
  const productUsage = new Map<ProductID, { totalPoundsUsed: number; totalGallonsUsed: number | null }>();

  let usingProductSolids = false;
  for (const { question, source } of questions) {
    if (!emissionsQuestionIsVisible(spec, question, column, periodStart, formulaChoicesByPeriod.get(column) ?? {})) {
      continue;
    }

    const answerKey = `${column}-${question.questionID}` as const;
    const answer = answersMap[answerKey];
    const isAnswered = answer !== undefined && answer !== '';

    questionsChecked.set(answerKey, { column, source, isAnswered });

    const isNA = answer === 'N/A';
    if (question.type === 'releaseProfileFormulaChoice') {
      if (typeof answer === 'string' && answer.startsWith('formula_choice_')) {
        releaseProfileFormulaChoices[question.choiceListID] = answer as ReleaseProfileFormulaChoiceID;
      }
    } else if (question.type === 'number') {
      if (isNA) {
        releaseProfileChemicalNaAnswers[question.questionID] = true;
      } else if (typeof answer === 'number') {
        releaseProfileAnswers[question.questionID] = answer;
      }
    } else if (question.type === 'productUsage') {
      if (typeof answer === 'number' && !archivedProductIds.has(question.productID)) {
        productUsage.set(question.productID, productUsageExpand(question, answer));
      }
    } else if (question.type === 'productSolids') {
      usingProductSolids = true;
    } else {
      const unexpected: never = question;
      console.error('calculateEmissions: Unexpected question type:', unexpected);
    }
  }

  let processForColumn = process;

  // In theory these are already provided as part of EmissionsProcessAndProfiles, but if the user updates their
  // answers, the UI doesn't update the process structure, so we need to apply them here:
  if (usingProductSolids) {
    processForColumn = {
      ...processForColumn,
      profiles: processForColumn.profiles.map((profile) => {
        return {
          ...profile,
          products: profile.products.map((product) => {
            return {
              ...product,
              solidsPercentageByWeight:
                (answersMap[`${column}-${productSolidsQuestionID(product.productID, 'weight')}`] as number) ??
                product.solidsPercentageByWeight,
              solidsPercentageByVolume:
                (answersMap[`${column}-${productSolidsQuestionID(product.productID, 'volume')}`] as number) ??
                product.solidsPercentageByVolume,
            };
          }),
        };
      }),
    };
  }

  // Exclude archived products from the process structure so their chemical contributions are not
  // included in calculations for this period:
  if (archivedProductIds.size > 0) {
    processForColumn = {
      ...processForColumn,
      profiles: processForColumn.profiles.map((profile) => {
        return {
          ...profile,
          products: profile.products.filter((product) => !archivedProductIds.has(product.productID)),
        };
      }),
    };
  }

  return {
    releaseProfileAnswers,
    releaseProfileChemicalNaAnswers,
    releaseProfileFormulaChoices,
    productUsage,
    process: processForColumn,
  };
}

export type EmissionsIntervalAnswerScope =
  | { type: 'facilityIntervalAnswers' }
  | { type: 'unitTypeIntervalAnswers'; emissionsUnitTypeID: EmissionsUnitTypeID; name: string }
  | { type: 'unitIntervalAnswers'; emissionsUnitID: EmissionsUnitID; name: string };

export function emissionsAnswerScopeLabel(scope: EmissionsIntervalAnswerScope): string {
  return scope.type === 'facilityIntervalAnswers' ? 'Facility' : scope.name;
}

type EmissionsIntervalAnswerScopeID =
  | 'facilityIntervalAnswers'
  | `unitTypeIntervalAnswers-${EmissionsUnitTypeID}`
  | `unitIntervalAnswers-${EmissionsUnitID}`;

export type EmissionsAnswerScopeKey = `${EmissionsIntervalAnswerScopeID}-${EmissionsAnswerKey}`;

export function emissionsAnswerScopeKey(
  scope: EmissionsIntervalAnswerScope,
  period: EmissionsPeriod | 'potential' | 'fixed@all',
  questionID: ReleaseProfileQuestionID,
): EmissionsAnswerScopeKey {
  let scopeID: EmissionsIntervalAnswerScopeID;
  if (scope.type === 'facilityIntervalAnswers') {
    scopeID = 'facilityIntervalAnswers';
  } else if (scope.type === 'unitTypeIntervalAnswers') {
    scopeID = `unitTypeIntervalAnswers-${scope.emissionsUnitTypeID}`;
  } else {
    scopeID = `unitIntervalAnswers-${scope.emissionsUnitID}`;
  }

  return `${scopeID}-${period}-${questionID}`;
}

function productUsageExpand(
  question: QuestionBriefProductUsage,
  answer: number,
): {
  totalPoundsUsed: number;
  totalGallonsUsed: null | number;
} {
  // productEdit blocks changes to usageUnits to prevent this conversion from becoming invalid at a future date:
  if (question.units === 'gallons') {
    const totalPoundsUsed = usageAmountToPounds(answer, question.units, question.specificGravity);
    if (typeof totalPoundsUsed === 'number') {
      return { totalPoundsUsed, totalGallonsUsed: answer };
    }
  }

  return {
    totalPoundsUsed: answer,
    totalGallonsUsed: null,
  };
}

export interface EmissionsProductUsageEntry {
  scope: EmissionsIntervalAnswerScope;
  questionID: ReleaseProfileQuestionID;
  productID: ProductID;
  units: UsageUnits;
  /** The answer as given in `units`: */
  value: number;
  poundsUsed: number;
  gallonsUsed: number | null;
}

function emissionsIntervalAnswerScope(
  unit: EmissionsUnitWithQuestionAndAnswerData,
  source: EmissionsQuestionSource,
): EmissionsIntervalAnswerScope | null {
  if (source === 'facilityIntervalAnswers') {
    return { type: 'facilityIntervalAnswers' };
  } else if (source === 'unitTypeIntervalAnswers') {
    return {
      type: 'unitTypeIntervalAnswers',
      emissionsUnitTypeID: unit.unitType.emissionsUnitTypeID,
      name: unit.unitType.name,
    };
  } else if (source === 'unitIntervalAnswers') {
    return { type: 'unitIntervalAnswers', emissionsUnitID: unit.emissionsUnitID, name: unit.unit.name };
  } else if (source === 'product' || source === 'unit' || source === 'unitType') {
    return null;
  } else {
    const unexpected: never = source;
    console.error('emissionsIntervalAnswerScope: Unexpected question source:', unexpected);
    return null;
  }
}

export interface EmissionsPeriodQuestionnaireState {
  period: EmissionsPeriodDetails;
  inputsAndOutputTypes: {
    releaseProfileAnswers: ReleaseProfileAnswerValues;
    releaseProfileChemicalNaAnswers: Partial<Record<`question_${string}`, boolean>>;
    releaseProfileFormulaChoices: QuestionnaireFormulaChoicesContainer;
    productUsage: Map<ProductID, { totalPoundsUsed: number; totalGallonsUsed: number | null }>;
    process: EmissionsProcessAndProfiles;
    outputType: FormulaOutputType;
  }[];
  questionsForInputTable: EmissionsInputTableQuestion[];
  /** Product usage recorded by this period: */
  productUsageEntries: EmissionsProductUsageEntry[];
  /**
   * Every cell this period read, by the column it was asked in. The counts below are this period's own view of them;
   * rolling the periods up into a unit's totals needs the cells themselves, since two periods computed from the same
   * column read the same cells.
   */
  questionsChecked: Map<EmissionsAnswerKey, EmissionsCellState>;
  questionsAnswered: number;
  questionsTotal: number;
}

export function* emissionsUnitAnnotatedIterateQuestionnaireStateByPeriod(
  unit: EmissionsUnitWithQuestionAndAnswerData,
  periodsUsing: EmissionsPeriodDetails[],
  productAnswersMap: ProductAnswersByIDAndType,
  releaseProfilesAndVariables: ReleaseProfilesAndVariables,
  questionnaireViewing: 'interval' | 'unit' | 'unitType',
): Generator<EmissionsPeriodQuestionnaireState, void, void> {
  const spec: EmissionsUnitQuestionnaireSpec = {
    unitType: unit.unitType,
    unit: unit.unit,
    chemicalsRelevant: unit.chemicals,
    process: unit.process,
    releaseProfilesAndVariables,
    chemicalNames: {}, // Don't need chemical names since we're just using this object for visibility checks.
    questionListCache: new Map(),
    questionVisibilityCache: new Map(),
  };

  for (const period of periodsUsing) {
    const { answersMapWithAnswersFromConfig, formulaChoicesByPeriod } = emissionsGatherAnswersForPeriod(
      unit,
      period,
      productAnswersMap,
    );
    const questionsChecked = new Map<EmissionsAnswerKey, EmissionsCellState>();

    const acceptableOutputTypes = unitPeriodToAcceptableOutputTypes(
      { trackingMode: unit.unit.trackingMode, limitsWithReleaseProfiles: unit.unit.limitsWithReleaseProfiles },
      period.period,
    );
    const inputsAndOutputTypes = Array.from(acceptableOutputTypes).map((outputType) => {
      const periodForInputs = emissionsAnswersPeriodForOutput(period.period, outputType);
      const inputs = emissionsAnswersPrepForCalculations(
        period.start, // Product archival and fixed configuration both depend on this calculation's date.
        unit.questions,
        unit.process,
        answersMapWithAnswersFromConfig,
        formulaChoicesByPeriod,
        spec,
        periodForInputs,
        questionsChecked,
      );

      return {
        ...inputs,
        outputType,
      };
    });

    let questionsAnswered = 0;
    for (const { isAnswered } of questionsChecked.values()) {
      if (isAnswered) {
        questionsAnswered++;
      }
    }

    const questionsForInputTable: EmissionsInputTableQuestion[] = [];
    const productUsageEntries: EmissionsProductUsageEntry[] = [];
    const formulaChoices = formulaChoicesByPeriod.get(period.period) ?? {};
    // Fixed-answer rows configure every output type, including PTE. Keep the actual row date for
    // formula-choice and product-archival visibility, and the original period key for saving answers.
    const periodForQuestionVisibility = questionnaireViewing === 'interval' ? period.period : 'fixed@all';
    for (const question of unit.questions) {
      if (
        !emissionsQuestionIsVisible(spec, question.question, periodForQuestionVisibility, period.start, formulaChoices)
      ) {
        continue;
      }

      if (
        (questionnaireViewing === 'interval' && (question.source === 'unit' || question.source === 'unitType')) ||
        (questionnaireViewing !== 'interval' && questionnaireViewing !== question.source)
      ) {
        continue;
      }

      questionsForInputTable.push(question as EmissionsInputTableQuestion);

      if (question.question.type === 'productUsage') {
        const scope = emissionsIntervalAnswerScope(unit, question.source);
        const value = answersMapWithAnswersFromConfig[`${period.period}-${question.question.questionID}`];
        if (scope && typeof value === 'number') {
          const expanded = productUsageExpand(question.question, value);
          productUsageEntries.push({
            scope,
            questionID: question.question.questionID,
            productID: question.question.productID,
            units: question.question.units,
            value,
            poundsUsed: expanded.totalPoundsUsed,
            gallonsUsed: expanded.totalGallonsUsed,
          });
        }
      }
    }

    yield {
      period,
      inputsAndOutputTypes,
      questionsForInputTable,
      productUsageEntries,
      questionsChecked,
      questionsAnswered,
      questionsTotal: questionsChecked.size,
    };
  }
}

export function emissionsUnitAnnotatedToQuestionsAndCalculations(
  unit: EmissionsUnitWithQuestionAndAnswerData,
  periodsUsing: EmissionsPeriodDetails[],
  productAnswersMap: ProductAnswersByIDAndType,
  releaseProfilesAndVariables: ReleaseProfilesAndVariables,
  questionnaireViewing: 'interval' | 'unit' | 'unitType',
): EmissionsQuestionsAndCalculationResultsForPeriod[] {
  const chemicalsSet = new Set(unit.chemicals);
  const chemicalSpecificQuestions = questionnaireFindChemicalSpecificQuestions(unit.questions.map((q) => q.question));
  const result: EmissionsQuestionsAndCalculationResultsForPeriod[] = [];
  for (const {
    period,
    inputsAndOutputTypes,
    questionsForInputTable,
    productUsageEntries,
    questionsChecked,
    questionsAnswered,
    questionsTotal,
  } of emissionsUnitAnnotatedIterateQuestionnaireStateByPeriod(
    unit,
    periodsUsing,
    productAnswersMap,
    releaseProfilesAndVariables,
    questionnaireViewing,
  )) {
    const calculated =
      // Fixed rows are a place to put answers, they are not real periods that can produce calculation results:
      period.type === 'fixed'
        ? {
            calculationLines: [],
            warnings: [],
          }
        : calculateEmissions(
            unit.unit,
            releaseProfilesAndVariables,
            chemicalsSet,
            chemicalSpecificQuestions,
            period,
            inputsAndOutputTypes,
          );

    result.push({
      unit,
      period,
      questionsForInputTable,
      productUsageEntries,
      questionsChecked,
      questionsAnswered,
      questionsTotal,
      ...calculated,
    });
  }
  return result;
}
